// PACKAGE #15 — Seller Ratings & Reviews (Phase B.2: write flow).
// Modeled on ../../CreateListing/BoostSheet.js's bottom-sheet pattern
// (createPortal to document.body, backdrop + card, inline loading/error
// states) — same interaction shape, different form content. One POST
// endpoint handles create/edit/reactivate (see reviewService.
// createOrUpdateReview's header comment) — this sheet does not need to
// know which of the three it is, only whether it's prefilled from an
// existing review (mode:'edit') or blank (mode:'create').
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAccessToken } from '../../../awsAuth';
import './ReviewFormSheet.scss';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API = process.env.REACT_APP_API || API_BASE;

const apiUrl = (path) => {
  const base = String(API || '').replace(/\/+$/, '');
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
};

// ---- pure helpers (exported for unit tests) ----------------------------

// Mirrors server/services/reviewService.js's MAX_TEXT_LENGTH. Not imported
// directly (that module pulls in server-only AWS SDK dependencies) — kept
// in sync by convention, same as the safe-internal-path duplication
// already established between server/routes/ads.js and AdBanner.js.
export const REVIEW_TEXT_MAX_LENGTH = 1000;

export function validateReviewForm({ rating, text }) {
  const errors = {};
  const r = Number(rating);
  if (!Number.isInteger(r) || r < 1 || r > 5) {
    errors.rating = 'Оберіть оцінку від 1 до 5 зірок';
  }
  const cleanText = typeof text === 'string' ? text.trim() : '';
  if (cleanText.length > REVIEW_TEXT_MAX_LENGTH) {
    errors.text = `Текст занадто довгий (максимум ${REVIEW_TEXT_MAX_LENGTH} символів)`;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function buildReviewPayload({ rating, text }) {
  const cleanText = typeof text === 'string' ? text.trim() : '';
  return { rating: Number(rating), text: cleanText || null };
}

// Maps a failed POST/DELETE response to a short, honest, non-technical
// message — never exposes moderation internals or raw error codes to the
// reviewer.
export function mapReviewSubmitError(status, errorCode) {
  if (status === 403) return 'Ви більше не можете залишити відгук цьому продавцю.';
  if (status === 409 && errorCode === 'REVIEW_HIDDEN_BY_MODERATION') {
    return 'Цей відгук приховано модерацією і не може бути відредагований.';
  }
  if (status === 409) return 'Не вдалося зберегти — спробуйте ще раз.';
  if (status === 400) return 'Перевірте дані форми.';
  return 'Не вдалося зберегти відгук. Спробуйте пізніше.';
}

export function isHiddenModerationError(status, errorCode) {
  return status === 409 && errorCode === 'REVIEW_HIDDEN_BY_MODERATION';
}

// Derives the form's starting values from (mode, initialReview) — pulled
// out of the reset-on-open effect specifically so the prefill rule ("edit
// mode with a review to prefill" vs. every other case) is unit-testable
// without rendering.
export function deriveInitialFormState({ mode, initialReview }) {
  if (mode === 'edit' && initialReview) {
    return { rating: Number(initialReview.rating) || 0, text: initialReview.text || '' };
  }
  return { rating: 0, text: '' };
}

const StarPicker = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;

  return (
    <div className="rfs-star-picker" role="radiogroup" aria-label="Оцінка">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`rfs-star ${n <= display ? 'is-filled' : ''}`}
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} з 5`}
          disabled={disabled}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
        >
          ★
        </button>
      ))}
    </div>
  );
};

export default function ReviewFormSheet({ open, onClose, sellerId, mode, initialReview, onSaved, onDeleted }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hiddenConflict, setHiddenConflict] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = deriveInitialFormState({ mode, initialReview });
    setRating(initial.rating);
    setText(initial.text);
    setFieldErrors({});
    setError('');
    setHiddenConflict(false);
    setSubmitting(false);
    setDeleting(false);
    setConfirmingDelete(false);
  }, [open, mode, initialReview]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleSubmit = async () => {
    if (submitting || deleting) return;
    setError('');

    const { valid, errors } = validateReviewForm({ rating, text });
    setFieldErrors(errors);
    if (!valid) return;

    setSubmitting(true);
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(apiUrl(`/api/reviews/${encodeURIComponent(sellerId)}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildReviewPayload({ rating, text })),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (isHiddenModerationError(res.status, data?.error)) {
          setHiddenConflict(true);
        }
        setError(mapReviewSubmitError(res.status, data?.error));
        return;
      }

      onSaved?.(data.review);
      onClose?.();
    } catch {
      setError(mapReviewSubmitError(0, null));
    } finally {
      setSubmitting(false);
    }
  };

  // Two-step in-app confirm (not window.confirm — native dialogs don't fire
  // reliably in every embedded/automated browser context, and an inline
  // confirm reads better here anyway: it stays inside the sheet instead of
  // popping a separate OS-level dialog). First click arms it; the actual
  // DELETE only fires on the second, explicit "Так, видалити" click.
  const handleDeleteClick = () => {
    if (submitting || deleting) return;
    setConfirmingDelete(true);
  };

  const handleCancelDelete = () => setConfirmingDelete(false);

  const handleDelete = async () => {
    if (submitting || deleting) return;

    setConfirmingDelete(false);
    setDeleting(true);
    setError('');
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(apiUrl(`/api/reviews/${encodeURIComponent(sellerId)}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(mapReviewSubmitError(res.status, data?.error));
        return;
      }

      onDeleted?.();
      onClose?.();
    } catch {
      setError(mapReviewSubmitError(0, null));
    } finally {
      setDeleting(false);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const busy = submitting || deleting;

  return createPortal(
    <div
      className="rfs-backdrop"
      onClick={(e) => {
        // Rendered via createPortal but still inside ReviewsList's React
        // tree — a click here would otherwise bubble (React portals bubble
        // through the component tree, not the DOM tree) up to
        // ReviewsList's own backdrop-close handler and close the whole
        // reviews modal behind this sheet. Stop it here, same guard
        // ReportsModal's own backdrop already uses.
        e.stopPropagation();
        onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="rfs-card" onClick={(e) => e.stopPropagation()}>
        <div className="rfs-title">{mode === 'edit' ? 'Редагувати відгук' : 'Залишити відгук'}</div>

        {hiddenConflict ? (
          <>
            <div className="rfs-error">{error}</div>
            <button type="button" className="rfs-close" onClick={onClose}>Закрити</button>
          </>
        ) : (
          <>
            <StarPicker value={rating} onChange={setRating} disabled={busy} />
            {fieldErrors.rating && <div className="rfs-field-error">{fieldErrors.rating}</div>}

            <textarea
              className="rfs-textarea"
              placeholder="Текст відгуку (необов'язково)"
              value={text}
              maxLength={REVIEW_TEXT_MAX_LENGTH + 200}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
              rows={4}
            />
            <div className="rfs-textlen">{text.trim().length}/{REVIEW_TEXT_MAX_LENGTH}</div>
            {fieldErrors.text && <div className="rfs-field-error">{fieldErrors.text}</div>}

            {error && <div className="rfs-error">{error}</div>}

            <button type="button" className="rfs-submit" onClick={handleSubmit} disabled={busy}>
              {submitting ? 'Збереження…' : mode === 'edit' ? 'Зберегти зміни' : 'Опублікувати'}
            </button>

            {mode === 'edit' && (
              confirmingDelete ? (
                <div className="rfs-delete-confirm">
                  <div className="rfs-delete-confirm__text">Видалити ваш відгук?</div>
                  <div className="rfs-delete-confirm__actions">
                    <button
                      type="button"
                      className="rfs-delete-confirm__yes"
                      onClick={handleDelete}
                      disabled={busy}
                    >
                      {deleting ? 'Видалення…' : 'Так, видалити'}
                    </button>
                    <button
                      type="button"
                      className="rfs-delete-confirm__no"
                      onClick={handleCancelDelete}
                      disabled={busy}
                    >
                      Скасувати
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="rfs-delete" onClick={handleDeleteClick} disabled={busy}>
                  Видалити відгук
                </button>
              )
            )}

            <button type="button" className="rfs-close" onClick={onClose} disabled={busy}>
              Скасувати
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
